import { Component, OnInit, Optional, Inject } from '@angular/core';
import { BrokerService } from './provider/broker.service';
import { BrokerSettings, ParsedBrokerField } from './provider/broker-settings.model';
import { FormGroup, UntypedFormControl, FormArray, Validators } from '@angular/forms';
import { NgxToastrService, StrategiesService } from '@shared/providers';
import { SavedBrokerSettings } from './provider/broker.interface';
import { find, size } from 'lodash-es';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { ConfirmationDialogService } from '../confirmation-dialog';
import { BROKER_CHANGE_CLIENT_ID_WARNING } from '@app/_shared/strings';

@Component({
  selector: 'app-add-broker',
  templateUrl: './add-broker.component.html',
  styleUrls: ['./add-broker.component.scss']
})
export class AddBrokerComponent implements OnInit {
  brokerSettingsModel: BrokerSettings;
  formArray: {
    field: ParsedBrokerField,
    control: UntypedFormControl
  }[] = [];
  brokerFormCtrl = new UntypedFormControl();
  savedSettings: SavedBrokerSettings;

  showPassword : boolean = true;

  constructor(
    private brokerService: BrokerService,
    private toastService: NgxToastrService,
    @Optional() @Inject(MAT_DIALOG_DATA) private editBroker: string,
    private dialogRef: MatDialogRef<AddBrokerComponent>,
    private confirmationService: ConfirmationDialogService,
    private strategiesService: StrategiesService
  ) { }

  ngOnInit(): void {
    this.fetchBrokerDetails();
    this.brokerFormCtrl.valueChanges.subscribe((value) => {
      this.generateBrokerForm();
    })
  }

  async fetchBrokerDetails() {
    await this.getBrokerFormSettings();
    await this.getSavedSettings();

    this.generateBrokerForm();
  }

  getBrokerFormSettings() {
    return new Promise<void>((resolve, rej) => {
      this.brokerService.getBrokerFormSettings().subscribe((res) => {
        this.brokerSettingsModel = res;
        if (this.editBroker) {
          this.brokerFormCtrl.setValue(this.editBroker);
        } else if (this.brokerSettingsModel.brokerKeys.size) {
          this.brokerFormCtrl.setValue(this.brokerSettingsModel.brokerKeys.keys().next().value);
        };
        resolve();
      })
    })
  }

  generateBrokerForm() {
    this.formArray = [];
    const savedSettings = this.savedSettings?.[this.brokerFormCtrl.value];

    this.brokerSettingsModel.brokerKeys.get(this.brokerFormCtrl.value).forEach((field) => {
      const control = new UntypedFormControl(null, field.isOptional ? [] : Validators.required);
      if (savedSettings) {
        control.setValue(savedSettings[field.name]);
      }
      this.formArray.push({
        control,
        field
      })
    });
  }

  getSavedSettings() {
    return new Promise((res, rej) => {
      this.brokerService.getBrokerSettings().subscribe((settings) => {
        this.savedSettings = settings;
        res(settings);
      })
    })
  }

  checkForUsernamechange() {
    let settings = {};
    for (let index = 0; index < this.formArray.length; index++) {
      const key = this.formArray[index];
      if (key.control.invalid) {
        this.formArray.forEach(field => field.control.markAsTouched());
        return;
      }
      settings[key.field.name] = key.control.value;
    }

    // RA-1292, If user tries to change broker username his existing strategies will be stopped because,
    // old stratgies were placing order for old clientID. So terminate old strategy First after giving
    // user warning.
    const savedSettings = this.savedSettings?.[this.brokerFormCtrl.value];
    if (savedSettings && savedSettings['clientID'] && savedSettings['clientID'] != settings['clientID']) {
      this.confirmationService.alert({
        message: BROKER_CHANGE_CLIENT_ID_WARNING,
        buttons: [
          {
            text: 'No',
          },
          {
            text: 'Yes',
            handler: () => {
              this.strategiesService.terminateAllStrategies();
              this.saveSettings(settings)
            },
            accent: true
          }
        ]
      })
    } else {
      this.saveSettings(settings);
    }
  }

  saveSettings(settings) {
    this.brokerService.saveBrokerFormSettings(this.brokerFormCtrl.value, settings).subscribe(() => {
      this.toastService.simpleCustomToaster(`Broker ${this.editBroker ? 'updated' : 'added'} successfully`);
      this.dialogRef.close();
    })
  }
}
